import React, { useMemo } from 'react';
import { DropdownItem, DropdownMenu, DropdownToggle, NavLink, UncontrolledDropdown } from 'src/components/ui';
import get from 'lodash/get';
import { Tab, TabGroupsJSONSchema, TabLeaf } from '../useTabGroupTree';
import { SelectTabHandler } from './types';
import { Bindings, Data, JnxTextExpr, useJnx } from '../../../../util/jnx';
import getPathFromId from '../../../../util/getPathFromId';

export type GroupedTabItemProp = {
  tab?: Tab | null;
  title?: string | null;
  index?: number;
  selection?: TabLeaf | null;
  onSelect: SelectTabHandler;
  schema: TabGroupsJSONSchema;
  dropdownItem?: boolean;
  rootFormData: Data;
  bindings: Bindings;
  toggleClassName?: string;
};

const GroupedTabItem = ({
  tab,
  title,
  index,
  selection,
  onSelect,
  schema,
  dropdownItem = false,
  rootFormData,
  bindings = {},
  toggleClassName = '',
}: GroupedTabItemProp) => {
  const tabTitle = title || tab?.title || `Tab ${(index ?? 0) + 1}`;

  const tabSelected = useMemo(() => {
    if (!tab) return false;
    return selection?.path.key.startsWith(tab.path.key);
  }, [tab, selection]);

  // if current "tab" is a field
  if (tab?.type === 'tabLeaf') {
    return (
      <LeafTabItem
        title={title}
        tab={tab}
        selection={selection}
        onSelect={onSelect}
        schema={schema}
        dropdownItem={dropdownItem}
        rootFormData={rootFormData}
        bindings={bindings}
        toggleClassName={toggleClassName}
      />
    );
  }

  const items = tab?.items;
  if (!items || items.length === 0) return null;

  if (items.length > 1) {
    return (
      <UncontrolledDropdown nav key={`grouped-tab-${index}`} active={tabSelected}>
        <DropdownToggle nav caret style={{ height: 'fit-content' }} className={toggleClassName}>
          {tabTitle}
        </DropdownToggle>
        <DropdownMenu container="body" style={{ maxHeight: '400px', overflow: 'auto' }}>
          {tab.items.map(subtab => {
            return subtab.type === 'tabLeaf' ? (
              <LeafTabItem
                key={`tab-${subtab.uid}`}
                tab={subtab}
                selection={selection}
                onSelect={onSelect}
                schema={schema}
                dropdownItem
                rootFormData={rootFormData}
                bindings={bindings}
                toggleClassName={toggleClassName}
              />
            ) : (
              <React.Fragment key={`grouped-tab-submenu-${subtab.uid}`}>
                {subtab.title && (
                  <DropdownItem header className="text-primary">
                    {subtab.title}
                  </DropdownItem>
                )}
                {(subtab.items || []).map((fieldId, ix) => (
                  <GroupedTabItem
                    key={ix}
                    tab={fieldId}
                    schema={schema}
                    dropdownItem
                    selection={selection}
                    onSelect={onSelect}
                    rootFormData={rootFormData}
                    bindings={bindings}
                    toggleClassName={toggleClassName}
                  />
                ))}
              </React.Fragment>
            );
          })}
        </DropdownMenu>
      </UncontrolledDropdown>
    );
  }

  return (
    <GroupedTabItem
      title={tab.title}
      tab={items[0]}
      schema={schema}
      dropdownItem={dropdownItem}
      selection={selection}
      onSelect={onSelect}
      rootFormData={rootFormData}
      bindings={bindings}
      toggleClassName={toggleClassName}
    />
  );
};

export type LeafTabItemProp = {
  tab: TabLeaf;
  title?: string | null;
  selection?: TabLeaf | null;
  onSelect: SelectTabHandler;
  schema: TabGroupsJSONSchema;
  dropdownItem?: boolean;
  rootFormData: Data;
  bindings: Bindings;
  toggleClassName?: string;
};

function LeafTabItem({
  title: _title,
  tab,
  selection,
  onSelect,
  schema,
  dropdownItem = false,
  rootFormData,
  bindings,
  toggleClassName,
}: LeafTabItemProp) {
  const fieldSchema = useMemo(() => schema.properties[tab.fieldName], [schema, tab]);

  const title = useMemo(() => _title || fieldSchema.title || fieldSchema.name, [fieldSchema]);

  const description = useMemo(() => {
    const desc = fieldSchema.description;
    return desc ? (
      <div className="flex flex-col pl-4 pr-2 gap-1">
        {desc?.split('<br>')?.map((d, ix) => (
          <small key={ix} style={{ maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {d}
          </small>
        ))}
      </div>
    ) : null;
  }, [fieldSchema]);

  const path = useMemo(() => getPathFromId(tab.fieldPathId.$id), [tab.fieldPathId]);

  const iconColorExpr = useJnx((fieldSchema['ui:tabGroupIconColor'] || '') as JnxTextExpr);
  const iconColor = useMemo(() => {
    if (!fieldSchema['ui:tabGroupIconColor']) return null;
    if (!iconColorExpr) return null;
    const formData = get(rootFormData, path) || {};
    return iconColorExpr.eval(formData, path, { ...bindings, formData });
  }, [iconColorExpr, rootFormData, path, bindings]);

  const icon = useMemo(() => {
    const tgIcon = fieldSchema['ui:tabGroupIcon'];
    if (!tgIcon) return null;

    return <span className={`fa mr-2 fa-${tgIcon} text-${iconColor || 'secondary'}`} />;
  }, [fieldSchema, iconColor]);

  return dropdownItem ? (
    <DropdownItem className="pr-2 text-ellipsis" onClick={() => onSelect(tab)} active={selection?.fieldName === tab.fieldName}>
      {icon}
      {title}
      {description}
    </DropdownItem>
  ) : (
    <NavLink className={`small ${toggleClassName}`} onClick={() => onSelect(tab)} active={selection?.fieldName === tab.fieldName}>
      {title}
    </NavLink>
  );
}

export default GroupedTabItem;
